import { FileRejection } from "react-dropzone";

/**
 * กติกาไฟล์แนบของประกาศข่าว ใช้ร่วมกันทั้ง addAnnounce (ประกาศทั่วไป/มาตรการช่วยเหลือ)
 * และ addAnnouncerental (ประกาศเช่าแผงค้า)
 *
 * ของเดิมทั้ง 2 ฟอร์มเก็บแค่ acceptedFiles[0] และเช็คนามสกุลเองในโค้ด
 * เลือก 3 ไฟล์พร้อมกันจึงได้ไฟล์เดียวแบบไม่แจ้งเตือน
 */

export const ANNOUNCE_MAX_FILES = 10;
export const ANNOUNCE_MAX_FILE_SIZE = 5 * 1024 * 1024;

/** react-dropzone 14 ต้องใช้ accept แบบ object — และให้ accept เป็นตัวคัดกรองเดียว ไม่เช็คนามสกุลซ้ำใน onDrop */
export const ANNOUNCE_ACCEPT_FILE = {
  "application/pdf": [".pdf"],
  "image/jpeg": [".jpg", ".jpeg"],
  "image/png": [".png"],
};

export type MergeDroppedResult = {
  files: any[];
  duplicated: number;
  overflow: number;
};

/** ชื่อไฟล์ของ item ในลิสต์ — File ใช้ .name, record จาก DB ใช้ .fileName */
const itemFileName = (item: any) =>
  (item instanceof File ? item.name : item?.fileName) || "";

/**
 * รวมไฟล์ที่เพิ่งลาก/เลือกเข้ากับลิสต์เดิม
 * `current` มีได้ทั้ง File ที่ยังไม่บันทึก และ record จาก DB (object ที่มี fileId)
 *
 * เทียบซ้ำด้วย "ชื่อไฟล์" ให้ตรงกับ backend ที่ dedupe by fileName และจับคู่ไฟล์เก่า-ใหม่
 * ด้วยชื่อไฟล์ (FRONTEND-FILE-UPLOAD-CHANGES.md ข้อ 2 + endpoint reference)
 * ถ้าเทียบด้วย size/lastModified เหมือนเดิม ไฟล์ชื่อซ้ำจะผ่านหน้าจอไปแล้วถูก backend ยุบ
 * ผู้ใช้เห็น 2 แถวแต่ได้ไฟล์เดียว — และชื่อที่ซ้ำกับไฟล์เดิมใน record ก็จะถูกจับคู่ทับ
 */
export const mergeDroppedFiles = (
  current: any[],
  acceptedFiles: File[]
): MergeDroppedResult => {
  const files = [...(current || [])];
  let duplicated = 0;
  let overflow = 0;

  (acceptedFiles || []).forEach((acceptedFile: File) => {
    if (files.length >= ANNOUNCE_MAX_FILES) {
      overflow += 1;
      return;
    }
    const isDuplicated = files.some(
      (x: any) => itemFileName(x) === acceptedFile.name
    );
    if (isDuplicated) {
      duplicated += 1;
      return;
    }
    files.push(acceptedFile);
  });

  return { files, duplicated, overflow };
};

/** ข้อความบอกสาเหตุที่ไฟล์ถูกปฏิเสธ — เดิมไฟล์ผิดชนิดถูกทิ้งเงียบ */
export const announceRejectMessage = (rejections: FileRejection[]) => {
  const code = rejections?.[0]?.errors?.[0]?.code;
  if (code === "file-too-large") {
    return `ไฟล์มีขนาดใหญ่เกิน ${ANNOUNCE_MAX_FILE_SIZE / (1024 * 1024)} MB`;
  }
  return "รองรับเฉพาะไฟล์ .pdf .jpg .png";
};

export type SyncAnnounceFilesResult = {
  /** จำนวนไฟล์ที่ลบไม่สำเร็จด้วยสาเหตุอื่นที่ไม่ใช่ 404 */
  deleteFailed: number;
  uploadFailed: boolean;
};

/**
 * จัดการไฟล์แนบทั้งหมดในจังหวะกดบันทึกครั้งเดียว — ลบของที่ถอดออกไว้ก่อน แล้วค่อยอัปโหลดของใหม่
 *
 * ตัวฟังก์ชัน **ไม่ throw** คืนสรุปผลให้หน้าที่เรียกไปเลือกข้อความเอง เพราะ record หลัก
 * ถูกบันทึกสำเร็จไปแล้วก่อนถึงจุดนี้ ถ้าปล่อย error ทะลุออกไปผู้ใช้จะเข้าใจว่าบันทึกไม่สำเร็จ
 * แล้วกดซ้ำจนได้ record ซ้ำ (แนวเดียวกับ ModalRepairAdditional.tsx:160-173)
 *
 * - `no_delete_old_file: "true"` = โหมดเพิ่มไฟล์ ไม่ให้ backend ล้างไฟล์เก่าของ refId+fileType
 *   ห้ามส่ง "false"/"0" เพราะ backend ตีความว่าให้ลบ (FRONTEND-FILE-UPLOAD-CHANGES.md ข้อ 1)
 * - `instanceof File` = record เดิมจาก DB เป็น object ธรรมดา ถ้า append ลงไปจะกลายเป็น "[object Object]"
 * - ชื่อ field `files[]` ถูกต้องแล้ว endpoint ใช้ AnyFilesInterceptor ไม่กรองชื่อ field
 *   แต่ห้าม append ทั้ง `files[]` และ `files` พร้อมกัน จะได้ไฟล์เข้าไป 2 เท่า
 */
export const syncAnnounceFiles = async (params: {
  res: any;
  files: any[];
  deletedFiles: any[];
  announceId?: any;
  deleteFile: (body: { fileType: string; refId: number; fileId: number }) => Promise<any>;
  uploadFiles: (formData: FormData) => Promise<any>;
  /** อ่าน http status จาก error — ส่ง getApiErrorStatus เข้ามา */
  errorStatus: (error: any) => number | undefined;
}): Promise<SyncAnnounceFilesResult> => {
  const { res, files, deletedFiles, announceId, deleteFile, uploadFiles, errorStatus } = params;
  let deleteFailed = 0;

  // try/catch ต่อใบ ไม่ใช่ครอบทั้ง loop — ใบเดียวพังต้องไม่ทำให้ใบที่เหลือและการอัปโหลดถูกข้าม
  for (const removed of deletedFiles || []) {
    try {
      await deleteFile({
        fileType: removed?.fileType || "announce",
        refId: removed?.refId || Number(announceId),
        fileId: removed?.fileId,
      });
    } catch (error) {
      // 404 = ไฟล์ไม่มีอยู่แล้ว ผลลัพธ์ตรงกับที่ผู้ใช้ต้องการ ไม่นับเป็นความล้มเหลว
      if (errorStatus(error) !== 404) deleteFailed += 1;
    }
  }

  const newFiles = (files || []).filter((item: any) => item instanceof File);
  if (newFiles.length === 0) return { deleteFailed, uploadFailed: false };

  const formData = new FormData();
  formData.append("refId", `${res?.data?.refId}`);
  formData.append("fileType", `${res?.data?.fileType}`);
  formData.append("no_delete_old_file", "true");
  newFiles.forEach((data: File) => formData.append("files[]", data));

  try {
    await uploadFiles(formData);
    return { deleteFailed, uploadFailed: false };
  } catch (error) {
    return { deleteFailed, uploadFailed: true };
  }
};

/**
 * ข้อความหลังบันทึก — record สำเร็จแล้วเสมอ ต่างกันแค่ผลของไฟล์แนบ
 * คืน `warn: true` เมื่อไฟล์มีปัญหา หน้าที่เรียกต้องพา user ออกจากหน้าเองแทน notiSuccess
 */
export const announceSaveResultMessage = (
  successMessage: string,
  result: SyncAnnounceFilesResult
): { message: string; warn: boolean } => {
  if (result.uploadFailed) {
    return {
      message: "บันทึกประกาศสำเร็จ แต่แนบไฟล์ไม่สำเร็จ กรุณาแก้ไขแล้วแนบไฟล์ใหม่อีกครั้ง",
      warn: true,
    };
  }
  if (result.deleteFailed > 0) {
    return {
      message: `บันทึกประกาศสำเร็จ แต่ลบไฟล์ไม่สำเร็จ ${result.deleteFailed} ไฟล์`,
      warn: true,
    };
  }
  return { message: successMessage, warn: false };
};
